import { getAccessToken } from '@lib/msal';
import type {
  AutomationRecord,
  ZapierAutomationSettings,
  TriggerAutomationRequest,
} from '@sca-uk/shared';

const apiBase = () => (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

// ── Settings ─────────────────────────────────────────────────────────────────

export async function fetchZapierAutomations(): Promise<ZapierAutomationSettings> {
  const res = await fetch(`${apiBase()}/api/settings/zapier-automations`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch automation settings');
  const data = await res.json();
  return data.data as ZapierAutomationSettings;
}

export async function saveZapierAutomations(
  automations: ZapierAutomationSettings['automations'],
): Promise<ZapierAutomationSettings> {
  const res = await fetch(`${apiBase()}/api/settings/zapier-automations`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ automations }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to save automation settings');
  }
  const data = await res.json();
  return data.data as ZapierAutomationSettings;
}

// ── Triggering ────────────────────────────────────────────────────────────────

/**
 * Trigger an automation and return the record ID for polling.
 */
export async function triggerAutomation(
  request: TriggerAutomationRequest,
): Promise<string> {
  const res = await fetch(`${apiBase()}/api/automations/trigger`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to trigger automation');
  }
  const data = await res.json();
  return (data.data as { id: string }).id;
}

// ── Status / History ──────────────────────────────────────────────────────────

export async function fetchAutomationStatus(id: string): Promise<AutomationRecord> {
  const res = await fetch(`${apiBase()}/api/automations/${id}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch automation status');
  const data = await res.json();
  return data.data as AutomationRecord;
}

export async function fetchPersonAutomations(personId: number): Promise<AutomationRecord[]> {
  const res = await fetch(`${apiBase()}/api/automations/person/${personId}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch automation history');
  const data = await res.json();
  return data.data as AutomationRecord[];
}
